import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AnimatePresence, motion } from 'framer-motion';
import Header from './components/Header';
import Footer from './components/Footer';
import CursorEffect from './components/CursorEffect';
import BackToTop from './components/BackToTop';
import ScrollProgress from './components/ScrollProgress';
import AnimatedBackground from './components/AnimatedBackground';
import ParticleField from './components/ParticleField';

import SearchBar from './components/SearchBar';
import { ToastProvider } from './components/ToastProvider';
import PageTransition from './components/PageTransition';
import GlobalErrorBoundary from './components/GlobalErrorBoundary';

// Import lazy loaded pages from routes config
import {
  Home, CreatorPage, ViewerPage, HoisterPage, AboutPage,
  ContactPage, PrivacyPage, TermsPage, CookiesPage,
  HowItWorksPage, DocumentationPage, APIReferencePage,
  CommunityPage, BlogPage
} from './routes';
import './index.css';

// Scroll to top component
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Premium Loading Fallback
const LoadingFallback = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-dark z-[100]">
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
      className="flex flex-col items-center"
    >
      <img src="/logo.png" alt="Loading" className="w-20 h-20 object-contain mb-4 animate-pulse" />
      <div className="h-1 w-32 bg-white/10 rounded-full overflow-hidden">
        <motion.div
          initial={{ x: "-100%" }}
          animate={{ x: "100%" }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          className="h-full w-full bg-primary"
        />
      </div>
    </motion.div>
  </div>
);

function App() {
  return (
    <GlobalErrorBoundary>
      <HelmetProvider>
        <ToastProvider>
          <Router>
            <ScrollToTop />
            <div className="min-h-screen bg-dark text-light">
              {/* Premium Effects Layer */}
              <AnimatedBackground variant="default" />
              <ParticleField particleCount={30} interactive={true} />
              <ScrollProgress />
              <SearchBar />

              {/* Original Effects */}
              <CursorEffect />
              <BackToTop />
              <Header />

              {/* Main Content with Page Transitions */}
              <main>
                <Suspense fallback={<LoadingFallback />}>
                  <AnimatePresence mode="wait">
                    <Routes>
                      <Route path="/" element={<PageTransition><Home /></PageTransition>} />
                      <Route path="/creator" element={<PageTransition><CreatorPage /></PageTransition>} />
                      <Route path="/viewer" element={<PageTransition><ViewerPage /></PageTransition>} />
                      <Route path="/hoister" element={<PageTransition><HoisterPage /></PageTransition>} />
                      <Route path="/about" element={<PageTransition><AboutPage /></PageTransition>} />
                      <Route path="/contact" element={<PageTransition><ContactPage /></PageTransition>} />
                      <Route path="/privacy" element={<PageTransition><PrivacyPage /></PageTransition>} />
                      <Route path="/terms" element={<PageTransition><TermsPage /></PageTransition>} />
                      <Route path="/cookies" element={<PageTransition><CookiesPage /></PageTransition>} />
                      <Route path="/how-it-works" element={<PageTransition><HowItWorksPage /></PageTransition>} />
                      <Route path="/documentation" element={<PageTransition><DocumentationPage /></PageTransition>} />
                      <Route path="/api-reference" element={<PageTransition><APIReferencePage /></PageTransition>} />
                      <Route path="/community" element={<PageTransition><CommunityPage /></PageTransition>} />
                      <Route path="/blog" element={<PageTransition><BlogPage /></PageTransition>} />
                    </Routes>
                  </AnimatePresence>
                </Suspense>
              </main>

              <Footer />
            </div>
          </Router>
        </ToastProvider>
      </HelmetProvider>
    </GlobalErrorBoundary>
  );
}

export default App;
